import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AppLayout from '../../components/ui/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import SectionCard from '../../components/ui/SectionCard';
import { useAuth } from '../../AuthContext';
import api from '../../api';
import tokens from '../../theme/tokens';

const VAZIO = { nome: '', email: '', torre: '', apartamento: '', nickName: '', dataNascimento: '', telefone: '', cpf: '', typePlayer: 'M', perfil: 'User', isento: false };

const mascaraCpf = (v) =>
  v.replace(/\D/g, '').slice(0, 11).replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
const mascaraTelefone = (v) =>
  v.replace(/\D/g, '').slice(0, 11).replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2');

/**
 * Cadastro de usuário pela diretoria (Admin/Diretor): o usuário já nasce ativo, recebe por e-mail uma
 * senha temporária e cria a própria senha no primeiro acesso. Se houver pelada aberta, entra nela.
 */
const CadastrarUsuarioPage = () => {
  const { userLogado } = useAuth();
  const navigate = useNavigate();
  const ehAdmin = userLogado?.perfil === 'Admin';
  const [form, setForm] = useState(VAZIO);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [criado, setCriado] = useState(null); // resposta da API
  const [copiado, setCopiado] = useState(false);

  const campo = (nome, transformar = (v) => v) => ({
    value: form[nome],
    onChange: (e) => setForm({ ...form, [nome]: transformar(e.target.value) }),
  });

  const nomeValido = form.nome.trim().split(/\s+/).length >= 2;
  const podeEnviar = nomeValido && /\S+@\S+\.\S+/.test(form.email) && form.torre && !enviando;

  const cadastrar = async (e) => {
    e.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      const { data } = await api.post('user/cadastro', { ...form, cpf: form.cpf.replace(/\D/g, ''), telefone: form.telefone.replace(/\D/g, '') });
      setCriado(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setErro(error.response?.data?.error || 'Não foi possível cadastrar o usuário.');
    }
    setEnviando(false);
  };

  const copiarSenha = async () => {
    try {
      await navigator.clipboard.writeText(criado.senhaTemporaria);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch (e) {
      // o texto continua visível para copiar manualmente
    }
  };

  const novoCadastro = () => {
    setCriado(null);
    setForm(VAZIO);
  };

  if (criado) {
    return (
      <AppLayout maxWidth="sm">
        <PageHeader eyebrow="Diretoria" title="Cadastrar usuário" />
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 64, color: 'success.main' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>{criado.user.nome} foi cadastrado</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>{criado.user.email}</Typography>

          <Stack spacing={1.5} sx={{ mt: 2.5, textAlign: 'left' }}>
            {criado.emailEnviado ? (
              <Alert severity="success">E-mail enviado com a senha temporária. No primeiro acesso, o jogador vai criar a própria senha.</Alert>
            ) : (
              <Alert severity="warning">
                Não foi possível enviar o e-mail. Repasse esta senha temporária ao jogador; no primeiro acesso ele cria a própria senha.
                <TextField
                  value={criado.senhaTemporaria || ''}
                  fullWidth
                  size="small"
                  sx={{ mt: 1.5, bgcolor: 'background.paper' }}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '1.1rem' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={copiarSenha} aria-label="Copiar senha"><ContentCopyRoundedIcon /></IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={copiado ? 'Senha copiada!' : undefined}
                />
              </Alert>
            )}
            {criado.incluidoNaPelada && (
              <Alert severity="info">Incluído na pelada aberta, em "Não confirmaram". Já dá para confirmar a presença dele.</Alert>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center" sx={{ mt: 3 }}>
            <Button variant="contained" startIcon={<PersonAddAlt1RoundedIcon />} onClick={novoCadastro}>Cadastrar outro</Button>
            {criado.incluidoNaPelada && <Button variant="outlined" onClick={() => navigate('/ordem-chegada')}>Ir para a Confirmação</Button>}
            <Button color="inherit" onClick={() => navigate('/user/aprovacao')}>Ver usuários</Button>
          </Stack>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout maxWidth="sm">
      <PageHeader
        eyebrow="Diretoria"
        title="Cadastrar usuário"
        subtitle="O jogador já entra ativo e recebe por e-mail uma senha temporária. Se houver pelada aberta, ele é incluído nela."
      />
      <Box component="form" onSubmit={cadastrar}>
        <SectionCard title="Dados do jogador">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Nome completo" required fullWidth {...campo('nome')} error={Boolean(form.nome) && !nomeValido} helperText={form.nome && !nomeValido ? 'Informe nome e sobrenome' : undefined} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="E-mail" type="email" required fullWidth {...campo('email', (v) => v.trim())} inputProps={{ autoCapitalize: 'none' }} helperText="A senha temporária é enviada para este e-mail" />
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Torre" required fullWidth {...campo('torre')}>
                <MenuItem value="Acqua">Acqua</MenuItem>
                <MenuItem value="Natura">Natura</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Apartamento" fullWidth {...campo('apartamento')} inputProps={{ inputMode: 'numeric' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Apelido" fullWidth {...campo('nickName')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Data de nascimento" type="date" fullWidth InputLabelProps={{ shrink: true }} {...campo('dataNascimento')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Telefone" fullWidth {...campo('telefone', mascaraTelefone)} inputProps={{ inputMode: 'tel' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="CPF" fullWidth {...campo('cpf', mascaraCpf)} inputProps={{ inputMode: 'numeric' }} helperText="Opcional · usado no PIX das mensalidades" />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="Na pelada" sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField select label="Tipo" fullWidth {...campo('typePlayer')}>
                <MenuItem value="M">Mensalista</MenuItem>
                <MenuItem value="D">Diarista</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Perfil" fullWidth {...campo('perfil')}>
                <MenuItem value="User">Jogador</MenuItem>
                <MenuItem value="Diretor">Diretor</MenuItem>
                {ehAdmin && <MenuItem value="Admin">Admin</MenuItem>}
              </TextField>
            </Grid>
            {ehAdmin && (
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={form.isento} onChange={(e) => setForm({ ...form, isento: e.target.checked })} />} label="Isento de mensalidade" />
              </Grid>
            )}
          </Grid>
        </SectionCard>

        {erro && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          startIcon={<PersonAddAlt1RoundedIcon />}
          disabled={!podeEnviar}
          sx={{ mt: 2, py: 1.5, bgcolor: tokens.color.brand }}
        >
          {enviando ? 'Cadastrando...' : 'Cadastrar e enviar senha'}
        </Button>
      </Box>
    </AppLayout>
  );
};

export default CadastrarUsuarioPage;
